import {
  Box,
  Stack,
  Grid,
  Typography,
  TextField,
  InputAdornment,
  CircularProgress,
  Button,
  Card,
  IconButton,
  Tooltip,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import MyLocationIcon from '@mui/icons-material/MyLocation';
import GpsFixedIcon from '@mui/icons-material/GpsFixed';
import GpsNotFixedIcon from '@mui/icons-material/GpsNotFixed';
import { ShoppingCart as ShoppingCartIcon } from '@mui/icons-material';
import CloseIcon from '@mui/icons-material/Close';
import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from 'react';
import GoogleMapReact from 'google-map-react';
import { environment } from '../../../environments/environment';
import logo from '../../../assets/fattyBattyLogo.png';
import truckImg from '../../../assets/Coffee Truck.png';
import { getGoogleMapsBootstrapConfig } from '../../Utils/googleMapsUtils';
import LocationCard from './LocationCard';
import LocationInput from '../../components/common/LocationInput';
import { useNavigate } from 'react-router';
import { useUserLocation } from '../../hooks/useUserLocation';
import { sortStoresByDistance } from '../../services/locationService';
import { FaEye } from 'react-icons/fa';
import StorefrontIcon from '@mui/icons-material/Storefront';
import { getStoreStatus } from '../../Utils/storeTimingUtils';
import SchedulingDialog from '../../components/SchedulingDialog/SchedulingDialog';
import { useDispatch, useSelector } from 'react-redux';
import { setUser, UpdateScheduledTime } from '@tabletuck/redux-slices';
import { auth, firestore } from '@tabletuck/firebase-services';
// Cluster marker component (for clustered locations)
const ClusterMarker = React.memo(({ cluster, onClusterClick, lat, lng }) => {
  const count = cluster.stores?.length || cluster.clusterSize;
  const navigate = useNavigate();
  return (
    <Box
      onClick={() => onClusterClick(cluster)}
      sx={{
        position: 'relative',
        cursor: 'pointer',
        transform: 'translate(-50%, -50%)',
        zIndex: 500,
      }}
    >
      <Box
        sx={{
          width: Math.min(50, 30 + count * 2),
          height: Math.min(50, 30 + count * 2),
          backgroundColor: environment.mainColor,
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          border: '3px solid white',
          boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
          transition: 'all 0.3s ease',
          '&:hover': {
            transform: 'translate(-50%, -50%) scale(1.1)',
            boxShadow: '0 6px 16px rgba(0,0,0,0.4)',
          },
        }}
      >
        <Typography
          sx={{
            color: 'white',
            fontWeight: 'bold',
            fontSize: count > 99 ? '10px' : count > 9 ? '12px' : '14px',
            fontFamily: 'Jost',
          }}
        >
          {count > 99 ? '99+' : count}
        </Typography>
      </Box>
    </Box>
  );
});

// Individual store marker component
const StoreMarker = React.memo(
  ({
    store,
    index,
    isSelected,
    showInfoCard,
    onMarkerClick,
    onClose,
    onScheduleClick,
    lat,
    lng,
  }) => {
    const shouldShowInfoCard = showInfoCard === store.id;
    const navigate = useNavigate();
    const formatAddress = (addr) => {
      if (!addr) return 'Address not available';
      if (typeof addr === 'string') return addr;

      // Normalize to US-style: line1, City, ST ZIP
      const line1 = addr.addressLine1 || addr.route || '';
      const city = addr.locality || addr.city || '';
      const state = addr.administrativeDistrictLevel1 || addr.state || '';
      const zip = addr.postalCode || addr.zipCode || '';

      const cityStateParts = [];
      if (city) cityStateParts.push(city);
      if (state) cityStateParts.push(state);

      let cityStateZip = cityStateParts.join(', ');
      if (zip) {
        cityStateZip = cityStateZip ? `${cityStateZip} ${zip}` : zip;
      }

      const parts = [line1, cityStateZip].filter(Boolean);
      if (parts.length) return parts.join(', ');

      return 'Address not available';
    };

    return (
      <Box
        onClick={() => onMarkerClick(store)}
        sx={{
          position: 'relative',
          cursor: 'pointer',
          transform: 'translate(-50%, -100%)',
          zIndex: isSelected ? 1000 : 1,
        }}
      >
        {/* Marker Image (Logo for individual markers) */}
        <Box
          sx={{
            width: isSelected ? 50 : 45,
            height: isSelected ? 50 : 45,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: '50%',
            overflow: 'hidden',
            backgroundColor: 'white',
            padding: '3px',
            border: `3px solid ${isSelected ? environment.mainColor : 'white'}`,
            boxShadow: isSelected
              ? '0 8px 20px rgba(0,0,0,0.22)'
              : '0 4px 12px rgba(0,0,0,0.18)',
            transition: 'all 0.25s ease',
            '& img': {
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              display: 'block',
            },
            '&:hover': {
              transform: isSelected ? 'scale(1.02)' : 'scale(1.05)',
            },
          }}
        >
          {(() => {
            const specialDeliveryIds = new Set([
              'L2AFG5YSPK15Q',
              'L46X7J42FCWX2',
            ]);
            const isSpecial = specialDeliveryIds.has(store?.id);

            return isSpecial ? (
              <StorefrontIcon
                sx={{ color: environment.mainColor, fontSize: 24 }}
              />
            ) : (
              <img
                src={truckImg}
                alt="Coffee Truck"
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              />
            );
          })()}
        </Box>

        {/* Info Card on Click */}
        {shouldShowInfoCard && (
          <Card
            sx={{
              position: 'absolute',
              bottom: '100%',
              left: '50%',
              transform: 'translateX(-50%)',
              mb: 1,
              p: 2,
              minWidth: '270px',
              maxWidth: '500px',
              zIndex: 1001,
              boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
              borderRadius: '12px',
              '&::after': {
                content: '""',
                position: 'absolute',
                top: '100%',
                left: '50%',
                transform: 'translateX(-50%)',
                width: 0,
                height: 0,
                borderLeft: '8px solid transparent',
                borderRight: '8px solid transparent',
                borderTop: '8px solid white',
              },
            }}
          >
            {/* Close button to dismiss the info card */}
            <IconButton
              size="small"
              onClick={(e) => {
                if (e && typeof e.stopPropagation === 'function') {
                  e.stopPropagation();
                }
                if (typeof onClose === 'function') onClose();
              }}
              aria-label="Close"
              sx={{
                position: 'absolute',
                top: 8,
                right: 8,
                zIndex: 1010,
                background: 'rgba(0,0,0,0.04)',
                '&:hover': { background: 'rgba(0,0,0,0.06)' },
              }}
            >
              <CloseIcon sx={{ fontSize: 18 }} />
            </IconButton>

            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 600,
                color: '#172B4D',
                fontFamily: 'Jost',
                fontSize: '14px',
                mb: 0.5,
              }}
            >
              {store.businessName || store.name}
            </Typography>

            <Tooltip
              title={formatAddress(store.address) || ''}
              placement="top"
              arrow
            >
              <Typography
                variant="body2"
                noWrap
                sx={{
                  color: '#6B7280',
                  fontSize: '12px',
                  fontFamily: 'Jost',
                  mb: 1,
                  maxWidth: 220,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  display: 'block',
                }}
              >
                {formatAddress(store.address)}
              </Typography>
            </Tooltip>

            {store.distance && (
              <Typography
                variant="caption"
                sx={{
                  color: environment.mainColor,
                  fontWeight: 600,
                  fontSize: '11px',
                  fontFamily: 'Jost',
                  display: 'block',
                  mb: 1,
                }}
              >
                <span role="img" aria-label="location">
                  📍
                </span>{' '}
                {store.distance.toFixed(2)} mi away
              </Typography>
            )}

            <Box
              sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}
            >
              <Button
                size="small"
                variant="outlined"
                startIcon={<FaEye size={14} />}
                aria-label={`Order now at ${store.businessName || store.name}`}
                onClick={(e) => {
                  if (e && typeof e.stopPropagation === 'function') {
                    e.stopPropagation();
                  }
                  // Navigate to store detail page or handle order action
                  navigate(`/locations/${store.id}`, { state: { store } });
                }}
                sx={{
                  borderColor: environment.mainColor,
                  color: environment.mainColor,
                  textTransform: 'none',
                  borderRadius: '999px',
                  fontSize: '12px',
                  fontWeight: 700,

                  '&:hover': {
                    boxShadow: '0 10px 26px rgba(255,107,53,0.22)',
                    borderColor: environment.mainColor,
                  },
                  '&:active': {
                    transform: 'translateY(0px) scale(0.995)',
                  },
                  transition: 'all 180ms ease',
                }}
              >
                More Info
              </Button>
              <Button
                size="small"
                variant="contained"
                startIcon={<ShoppingCartIcon sx={{ fontSize: 14 }} />}
                aria-label={`Order now at ${store.businessName || store.name}`}
                onClick={(e) => {
                  if (e && typeof e.stopPropagation === 'function') {
                    e.stopPropagation();
                  }
                  navigate('/menu', { state: { openBranchDialog: true } });
                }}
                sx={{
                  background: `linear-gradient(135deg, ${environment.mainColor} 0%, ${environment.mainColor}CC 100%)`,
                  color: '#fff',
                  textTransform: 'none',
                  borderRadius: '999px',
                  fontSize: '12px',
                  fontWeight: 700,
                  boxShadow: '0 8px 22px rgba(255,107,53,0.18)',
                  '&:hover': {
                    boxShadow: '0 10px 26px rgba(255,107,53,0.22)',
                  },
                  textShadow: '0 1px 3px rgba(0, 0, 0, 0.3)',
                  '&:active': {
                    transform: 'translateY(0px) scale(0.995)',
                  },
                  transition: 'all 180ms ease',
                }}
              >
                Order Now
              </Button>
            </Box>
          </Card>
        )}
      </Box>
    );
  }
);

// Combined map marker component that handles both individual stores and clusters
const MapMarker = React.memo(
  ({
    store,
    stores,
    number,
    isSelected,
    onClick,
    isCluster,
    clusterSize,
    showInfoCard,
    onClose,
    onScheduleClick,
  }) => {
    const handleClick = useCallback(
      (e) => {
        if (e && typeof e.stopPropagation === 'function') {
          e.stopPropagation();
        }
        if (onClick) {
          onClick(isCluster ? stores : store);
        }
      },
      [store?.id, stores, number, isCluster, onClick]
    );

    if (isCluster) {
      return (
        <ClusterMarker
          cluster={{ stores, clusterSize }}
          onClusterClick={handleClick}
        />
      );
    }

    return (
      <StoreMarker
        store={store}
        index={number}
        isSelected={isSelected}
        showInfoCard={showInfoCard}
        onMarkerClick={handleClick}
        onClose={onClose}
        onScheduleClick={onScheduleClick}
      />
    );
  },
  (prevProps, nextProps) => {
    // Only re-render if props that affect rendering actually change
    return (
      prevProps.store?.id === nextProps.store?.id &&
      prevProps.stores?.length === nextProps.stores?.length &&
      prevProps.number === nextProps.number &&
      prevProps.isSelected === nextProps.isSelected &&
      prevProps.isCluster === nextProps.isCluster &&
      prevProps.clusterSize === nextProps.clusterSize &&
      prevProps.showInfoCard === nextProps.showInfoCard
    );
  }
);
const Locations = () => {
  const [stores, setStores] = useState([]);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const userData = useSelector((state) => state.authReducer.userData);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
  const [selectedStore, setSelectedStore] = useState(null);
  const [showManualLocation, setShowManualLocation] = useState(false);
  const [mapCenter, setMapCenter] = useState({
    lat: 36.3882822,
    lng: -86.4486959,
  });
  const [mapKey, setMapKey] = useState(0);
  const [mapZoom, setMapZoom] = useState(12);
  const [showInfoCard, setShowInfoCard] = useState(null); // Control which marker shows info card
  const [showSchedulingDialog, setShowSchedulingDialog] = useState(false);
  const [pendingStoreForScheduling, setPendingStoreForScheduling] =
    useState(null);
  const mapRef = useRef(null);
  const locationsListRef = useRef(null);
  const locationCardRefs = useRef({});

  // Use the reusable location hook
  const {
    userLocation,
    isGettingLocation,
    locationError,
    getLocationWithFallbacks,
    setLocationFromAddress,
  } = useUserLocation({ autoStart: true, enableConsoleLogging: true });

  // Utility function to calculate distance between two points
  const calculateDistance = useCallback((lat1, lng1, lat2, lng2) => {
    const R = 6371; // Earth's radius in km
    const dLat = ((lat2 - lat1) * Math.PI) / 180;
    const dLng = ((lng2 - lng1) * Math.PI) / 180;
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos((lat1 * Math.PI) / 180) *
        Math.cos((lat2 * Math.PI) / 180) *
        Math.sin(dLng / 2) *
        Math.sin(dLng / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  }, []);

  // Improved clustering function with better UX
  const clusterMarkers = useCallback((markers, zoom) => {
    // Clustering disabled: always return individual markers so every store
    // is rendered as its own marker. This preserves marker ordering and
    // props while ensuring no cluster UI is produced.
    if (!markers || markers.length === 0) return [];
    return markers.map((m) => ({ ...m, isCluster: false }));
  }, []);

  // Handle marker click (both individual and cluster)
  const handleMarkerClick = useCallback((storeOrStores) => {
    if (Array.isArray(storeOrStores)) {
      // Cluster click - zoom in intelligently to break apart the cluster
      const stores = storeOrStores;
      const avgLat =
        stores.reduce((sum, store) => {
          const lat = store._geoloc?.lat || store.address?.lat;
          return sum + (lat || 0);
        }, 0) / stores.length;

      const avgLng =
        stores.reduce((sum, store) => {
          const lng = store._geoloc?.lng || store.address?.lng;
          return sum + (lng || 0);
        }, 0) / stores.length;

      setMapCenter({ lat: avgLat, lng: avgLng });

      // Smart zoom increment based on current zoom and cluster size
      setMapZoom((prevZoom) => {
        let newZoom;
        if (prevZoom < 10) newZoom = 12;
        else if (prevZoom < 12) newZoom = 14;
        else if (prevZoom < 14) newZoom = 16;
        else if (prevZoom < 16) newZoom = 17;
        else newZoom = Math.min(prevZoom + 1, 19);

        return newZoom;
      });

      // Hide any open info cards when clicking cluster
      setShowInfoCard(null);
    } else {
      // Individual store click
      const store = storeOrStores;
      setSelectedStore((prevSelected) => {
        if (prevSelected?.id === store.id) {
          return prevSelected;
        }
        return store;
      });

      // Show/hide info card for this store
      setShowInfoCard((prevShowInfoCard) => {
        return prevShowInfoCard === store.id ? null : store.id;
      });

      // Smooth scroll to the corresponding card in the list
      scrollToLocationCard(store.id);

      // Update map center
      const lat = store?._geoloc?.lat;
      const lng = store?._geoloc?.lng;

      if (lat && lng) {
        setMapCenter((prevCenter) => {
          if (
            Math.abs(prevCenter.lat - lat) < 0.0001 &&
            Math.abs(prevCenter.lng - lng) < 0.0001
          ) {
            return prevCenter;
          }
          return { lat, lng };
        });
      }
    }
  }, []);

  // Handle map changes (zoom/pan)
  const handleMapChange = useCallback(({ zoom }) => {
    setMapZoom(zoom);
  }, []);

  // Handle list card click
  const handleListCardClick = useCallback(
    (store) => {
      // Move map to selected store location
      const lat = store._geoloc?.lat || store.address?.lat;
      const lng = store._geoloc?.lng || store.address?.lng;

      if (lat && lng) {
        setMapCenter({ lat: Number(lat), lng: Number(lng) });
        // Set a good zoom level to show the store clearly
        setMapZoom((prevZoom) => Math.max(prevZoom, 15));
      }

      // Select the store on the map
      setSelectedStore(store);
      // Show the info card for this store
      setShowInfoCard(store.id);
    },
    [navigate]
  );

  // Handle closing info card
  const handleCloseInfoCard = useCallback(() => {
    setShowInfoCard(null);
    setSelectedStore(null);
  }, []);

  // Handle schedule click from map marker
  const handleScheduleClick = useCallback((store) => {
    setPendingStoreForScheduling(store);
    setShowSchedulingDialog(true);
  }, []);

  // Handle scheduling confirmation
  const handleSchedulingConfirm = useCallback(
    (schedulingData) => {
      if (!pendingStoreForScheduling) return;

      // Store scheduling data in Redux with storeId to track which branch it's for
      dispatch(
        UpdateScheduledTime({
          ...schedulingData,
          storeId: pendingStoreForScheduling.id,
        })
      );

      // Determine order type based on store capabilities
      const orderType = pendingStoreForScheduling.delivery_enabled
        ? 'delivery'
        : 'pickup';

      // Update user preferences
      const updatedUser = {
        ...(userData || {}),
        pickupOrDelivery: orderType,
        defaultStore: pendingStoreForScheduling.id,
      };
      dispatch(setUser({ userData: updatedUser }));

      if (auth?.currentUser?.uid) {
        const uid = auth.currentUser.uid;
        firestore
          .collection('users')
          .doc(uid)
          .set(
            {
              pickupOrDelivery: orderType,
              defaultStore: pendingStoreForScheduling.id,
            },
            { merge: true }
          )
          .catch((e) => console.warn('Failed to persist after scheduling:', e));
      }

      // Close dialog
      setShowSchedulingDialog(false);
      setPendingStoreForScheduling(null);

      // Navigate to menu
      navigate(`/menu/store/${pendingStoreForScheduling.id}`, {
        state: { store: pendingStoreForScheduling },
      });
    },
    [pendingStoreForScheduling, userData, dispatch, navigate]
  );

  // Handle scheduling dialog close
  const handleSchedulingClose = useCallback(() => {
    setShowSchedulingDialog(false);
    setPendingStoreForScheduling(null);
  }, []);

  // Smooth scroll to location card in the list
  const scrollToLocationCard = useCallback((storeId) => {
    const cardElement = locationCardRefs.current[storeId];
    const listContainer = locationsListRef.current;

    if (cardElement && listContainer) {
      // Use getBoundingClientRect for more accurate positioning
      const cardRect = cardElement.getBoundingClientRect();
      const containerRect = listContainer.getBoundingClientRect();

      // Calculate the card's position relative to the container
      const cardRelativeTop = cardRect.top - containerRect.top;
      const containerHeight = listContainer.clientHeight;
      const cardHeight = cardRect.height;

      // Calculate target scroll position to center the card in the view
      // Add current scroll position and adjust for centering
      const currentScrollTop = listContainer.scrollTop;
      const targetScrollTop =
        currentScrollTop +
        cardRelativeTop -
        containerHeight / 2 +
        cardHeight / 2;

      // Smooth scroll to the target position
      listContainer.scrollTo({
        top: Math.max(0, targetScrollTop),
        behavior: 'smooth',
      });
    }
  }, []);

  // Handle search input change
  const handleSearchChange = useCallback((event) => {
    setSearchQuery(event.target.value);
  }, []);

  // Handle location updates
  // Helper to find the closest store to a given location
  const findClosestStoreToLocation = useCallback(
    (storesArr, location) => {
      if (!storesArr || storesArr.length === 0 || !location) return null;
      let closest = null;
      let minDist = Infinity;
      storesArr.forEach((store) => {
        const lat = store._geoloc?.lat || store.address?.lat;
        const lng = store._geoloc?.lng || store.address?.lng;
        if (!lat || !lng) return;
        const d = calculateDistance(
          location.lat,
          location.lng,
          Number(lat),
          Number(lng)
        );
        if (d < minDist) {
          minDist = d;
          closest = store;
        }
      });
      return closest;
    },
    [calculateDistance]
  );

  // Always center the map on the closest branch (to the user if available,
  // otherwise to a sensible default). We intentionally do NOT center the
  // map on the raw user location so the user's location is not shown on the map.
  useEffect(() => {
    if (!stores || stores.length === 0) return;

    const DEFAULT_CENTER = { lat: 36.3882822, lng: -86.4486959 };

    let targetStore = null;

    if (userLocation) {
      targetStore = findClosestStoreToLocation(stores, userLocation);
    }

    // Fallback: pick the store closest to the default center, or the first store
    if (!targetStore) {
      targetStore =
        stores.reduce((acc, s) => {
          const lat = s._geoloc?.lat || s.address?.lat;
          const lng = s._geoloc?.lng || s.address?.lng;
          if (!lat || !lng) return acc;
          if (!acc) return s;
          const accLat = acc._geoloc?.lat || acc.address?.lat;
          const accLng = acc._geoloc?.lng || acc.address?.lng;
          const accD = calculateDistance(
            DEFAULT_CENTER.lat,
            DEFAULT_CENTER.lng,
            Number(accLat),
            Number(accLng)
          );
          const sD = calculateDistance(
            DEFAULT_CENTER.lat,
            DEFAULT_CENTER.lng,
            Number(lat),
            Number(lng)
          );
          return sD < accD ? s : acc;
        }, null) || stores[0];
    }

    if (targetStore) {
      const lat = targetStore._geoloc?.lat || targetStore.address?.lat;
      const lng = targetStore._geoloc?.lng || targetStore.address?.lng;
      if (lat && lng) {
        setMapCenter({ lat: Number(lat), lng: Number(lng) });
        setSelectedStore(targetStore);
      }
    }
  }, [stores, userLocation, findClosestStoreToLocation, calculateDistance]);

  // Debounce search query for better performance
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Fetch stores from Firebase
  useEffect(() => {
    const fetchStores = async () => {
      try {
        setLoading(true);
        const storesSnapshot = await firestore
          .collection('stores')
          .where('is_deleted', '==', false)
          .get();

        const storesData = storesSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));

        setStores(storesData);

        // We intentionally do NOT set the map center here. The map center
        // is determined by the effect that finds the closest branch to the
        // user (or the default center) so the map always begins focused on
        // the nearest branch.
      } catch (error) {
        console.error('Error fetching stores:', error);
        setStores([]);
      } finally {
        setLoading(false);
      }
    };

    fetchStores();
  }, []);

  // Filtered stores (with distance sorting if user location available)
  const filteredStoresForList = useMemo(() => {
    let filtered = stores;

    if (debouncedSearchQuery.trim()) {
      const query = debouncedSearchQuery.toLowerCase();
      filtered = stores.filter((store) => {
        // Combine all address fields into a single string for easier search
        const addressString = [
          store.address?.displayName,
          store.address?.addressLine1,
          store.address?.addressLine2,
          store.address?.city,
          store.address?.state,
          store.address?.zip,
          store.address?.country,
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        return (
          store.businessName?.toLowerCase().includes(query) ||
          addressString.includes(query)
        );
      });
    }

    // Sort by distance if user location is available
    if (userLocation) {
      return sortStoresByDistance(filtered, userLocation);
    }

    return filtered;
  }, [stores, debouncedSearchQuery, userLocation]);

  // Map markers with clustering
  const mapMarkers = useMemo(() => {
    const markers = filteredStoresForList
      .map((store, index) => {
        const lat = store._geoloc?.lat || store.address?.lat;
        const lng = store._geoloc?.lng || store.address?.lng;

        if (!lat || !lng) {
          return null;
        }

        return {
          lat: Number(lat),
          lng: Number(lng),
          store,
          number: index + 1,
          id: store.id,
          key: `marker-${store.id}-${index}`,
        };
      })
      .filter(Boolean);

    // Apply clustering based on current zoom level
    return clusterMarkers(markers, mapZoom);
  }, [filteredStoresForList, mapZoom, clusterMarkers]);

  // Hide info card when stores change or component unmounts
  useEffect(() => {
    setShowInfoCard(null);
  }, [stores]);

  // Map options
  const mapOptions = useMemo(
    () => ({
      styles: [
        {
          featureType: 'poi',
          elementType: 'labels',
          stylers: [{ visibility: 'off' }],
        },
        {
          featureType: 'transit',
          elementType: 'labels',
          stylers: [{ visibility: 'off' }],
        },
      ],
      gestureHandling: 'greedy',
      zoomControl: true,
      mapTypeControl: false,
      scaleControl: false,
      streetViewControl: false,
      rotateControl: false,
      fullscreenControl: false,
      disableDefaultUI: false,
      maxZoom: 20,
      minZoom: 2,
      clickableIcons: false,
      keyboardShortcuts: false,
    }),
    []
  );

  return (
    <Box
      variant="section"
      sx={{ flexGrow: 1, minHeight: '85vh', background: '#fff' }}
    >
      <Grid container spacing={0} sx={{ minHeight: '85vh' }}>
        {/* Sidebar: Locations List */}
        <Grid
          item
          xs={12}
          md={5}
          lg={4}
          sx={{
            background: '#FAFAFA',
            borderRight: { md: '1px solid #eee' },
            minHeight: '85vh',
            p: { xs: 2, md: 4 },
            overflowY: 'auto',
          }}
        >
          {/* Header */}
          <Box
            sx={{
              mb: 2,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
            }}
          >
            <Box>
              <Typography
                variant="span"
                sx={(theme) => ({
                  color: environment.mainColor,
                  fontSize: '17px',
                  fontWeight: 700,
                  textTransform: 'capitalize',
                  letterSpacing: '1px',
                  fontFamily: '"Oswald", sans-serif',
                  [theme.breakpoints.down('sm')]: {
                    fontSize: '14px',
                    marginBottom: theme.spacing(1),
                  },
                })}
              >
                Our Locations
              </Typography>
              <Typography
                variant="h2"
                sx={(theme) => ({
                  fontSize: '20px',
                  fontWeight: 700,
                  lineHeight: '46x',
                  color: '#172B4D',
                  letterSpacing: '0%',
                  fontFamily: '"Oswald", sans-serif',
                  textTransform: 'uppercase',
                  [theme.breakpoints.down('sm')]: {
                    fontSize: '16px',
                    marginBottom: theme.spacing(2),
                  },
                })}
              >
                find a location near you
              </Typography>
            </Box>
          </Box>
          <Box sx={{ mb: 2 }}>
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Search by restaurant name or location"
              value={searchQuery}
              onChange={handleSearchChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: '#B0B0B0', fontSize: 22 }} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    {isGettingLocation ? (
                      <GpsNotFixedIcon
                        sx={{
                          color: '#FFA726',
                          fontSize: 22,
                          cursor: 'pointer',
                          animation: 'pulse 1.5s infinite',
                          '@keyframes pulse': {
                            '0%': { opacity: 1 },
                            '50%': { opacity: 0.5 },
                            '100%': { opacity: 1 },
                          },
                        }}
                        onClick={getLocationWithFallbacks}
                        title="Getting your location..."
                      />
                    ) : userLocation ? (
                      <GpsFixedIcon
                        sx={{
                          color: '#4CAF50',
                          fontSize: 22,
                          cursor: 'pointer',
                        }}
                        onClick={getLocationWithFallbacks}
                        title={`Location found via ${
                          userLocation.method || 'GPS'
                        } - Click to refresh`}
                      />
                    ) : (
                      <MyLocationIcon
                        sx={{
                          color: locationError ? '#F44336' : '#212121',
                          fontSize: 22,
                          cursor: 'pointer',
                        }}
                        onClick={getLocationWithFallbacks}
                        title={locationError || 'Click to get your location'}
                      />
                    )}
                  </InputAdornment>
                ),
                sx: {
                  borderRadius: '32px',
                  background: '#33333305',
                  fontFamily: 'poppins, sans-serif',
                  fontSize: 16,
                  height: '48px',
                  padding: '12px 24px',
                  '& input::placeholder': {
                    color: '#828282',
                    fontFamily: 'poppins, sans-serif',
                    fontSize: 16,
                    opacity: 1,
                  },
                },
              }}
              inputProps={{
                style: {
                  fontFamily: 'Jost, sans-serif',
                  fontWeight: 500,
                  fontSize: 16,
                },
              }}
            />
          </Box>

          {/* Reusable Location Input Component */}
          <LocationInput
            userLocation={userLocation}
            isGettingLocation={isGettingLocation}
            locationError={locationError}
            onGetLocation={getLocationWithFallbacks}
            onSetLocationFromAddress={setLocationFromAddress}
            showManualInput={showManualLocation}
            onToggleManualInput={setShowManualLocation}
            placeholder="Enter your city or address..."
            showLocationIcon={false} // We already have it in the search bar
          />

          {/* Locations List */}
          <Stack
            ref={locationsListRef}
            gap={2}
            sx={{ overflowY: 'auto', maxHeight: '60vh' }}
          >
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress color="primary" />
              </Box>
            ) : filteredStoresForList.length > 0 ? (
              filteredStoresForList.map((store, index) => (
                <Box
                  key={store.id}
                  ref={(el) => {
                    if (el) {
                      locationCardRefs.current[store.id] = el;
                    } else {
                      delete locationCardRefs.current[store.id];
                    }
                  }}
                >
                  <LocationCard
                    store={store}
                    number={index + 1}
                    userLocation={userLocation}
                    onClick={() => handleListCardClick(store)}
                    isSelected={selectedStore?.id === store.id}
                  />
                </Box>
              ))
            ) : (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant="body1" color="text.secondary">
                  {searchQuery
                    ? 'No stores found matching your search.'
                    : 'No stores available.'}
                </Typography>
              </Box>
            )}
          </Stack>
        </Grid>
        {/* Main: Map Area */}
        <Grid
          item
          xs={12}
          md={7}
          lg={8}
          sx={{
            position: 'relative',
            minHeight: { xs: 300, md: '85vh' },
            p: 0,
          }}
        >
          {/* Google Map */}
          <Box
            sx={{
              width: '100%',
              height: { xs: 300, md: '85vh' },
              position: 'relative',
            }}
          >
            <GoogleMapReact
              key={mapKey}
              ref={mapRef}
              bootstrapURLKeys={getGoogleMapsBootstrapConfig()}
              center={mapCenter}
              zoom={mapZoom}
              options={mapOptions}
              onGoogleApiLoaded={({ map, maps }) => {
                mapRef.current = { map, maps };
              }}
              onChange={handleMapChange}
              onChildClick={() => {
                // Child click handler
              }}
              resetBoundsOnResize={false}
              yesIWantToUseGoogleMapApiInternals
            >
              {mapMarkers.map((marker) => (
                <MapMarker
                  key={marker.key}
                  lat={marker.lat}
                  lng={marker.lng}
                  store={marker.store}
                  stores={marker.stores}
                  number={marker.number}
                  clusterSize={marker.clusterSize}
                  isSelected={
                    !marker.isCluster && selectedStore?.id === marker.store?.id
                  }
                  isCluster={marker.isCluster}
                  showInfoCard={showInfoCard}
                  onClick={handleMarkerClick}
                  onClose={handleCloseInfoCard}
                  onScheduleClick={handleScheduleClick}
                />
              ))}
            </GoogleMapReact>
          </Box>
        </Grid>
      </Grid>

      {/* Scheduling Dialog - shown for both open and closed branches */}
      <SchedulingDialog
        open={showSchedulingDialog}
        onClose={handleSchedulingClose}
        onConfirm={handleSchedulingConfirm}
        store={pendingStoreForScheduling}
        orderType={
          pendingStoreForScheduling?.delivery_enabled ? 'delivery' : 'pickup'
        }
        isStoreOpen={
          pendingStoreForScheduling
            ? getStoreStatus(pendingStoreForScheduling).isOpen
            : undefined
        }
      />
    </Box>
  );
};

export default Locations;
